import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { CourseCard } from '../components/CourseCard';
import { useCourses, categories } from '../hooks/useCourses';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Users, Award, Headphones, ChevronRight, Loader2, Calendar, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import ScheduleCalendar from '../components/ScheduleCalendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const { t, language } = useLanguage();
  const { courses, loading, getFeaturedCourses } = useCourses();
  const featuredCourses = getFeaturedCourses();
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/api/schedules`);
      // Get all schedules for calendar display (open, full, closed)
      const allSchedules = response.data
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      setSchedules(allSchedules);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const [selectedFeature, setSelectedFeature] = useState(null);

  // Top page course list: featured courses are pinned at the top,
  // the remaining slots rotate through the other courses.
  const SLOTS = 6;
  const pinnedCourses = featuredCourses.slice(0, SLOTS);
  const otherCourses = courses.filter(c => !c.featured);
  const rotateCount = Math.max(SLOTS - pinnedCourses.length, 0);
  const [rotationIndex, setRotationIndex] = useState(0);

  useEffect(() => {
    if (rotateCount === 0 || otherCourses.length <= rotateCount) return;
    const timer = setInterval(() => {
      setRotationIndex(prev => (prev + rotateCount) % otherCourses.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [rotateCount, otherCourses.length]);

  const rotatingCourses = rotateCount > 0 && otherCourses.length > 0
    ? Array.from({ length: Math.min(rotateCount, otherCourses.length) }, (_, k) =>
        otherCourses[(rotationIndex + k) % otherCourses.length])
    : [];
  const displayCourses = [...pinnedCourses, ...rotatingCourses];

  const features = [
    {
      icon: Award,
      title: t('about.features.certified'),
      description: t('about.features.certifiedDesc'),
      detail: '労働安全衛生法に基づく技能講習・特別教育を正式に実施する認定教育機関です。フォークリフト、クレーン、建設機械など各種資格取得に対応し、修了証を発行いたします。',
    },
    {
      icon: Users,
      title: t('about.features.multilingual'),
      description: t('about.features.multilingualDesc'),
      detail: '日本語のほか、英語・ポルトガル語・インドネシア語・ベトナム語・スペイン語・タガログ語の7言語に対応。外国人労働者の方も母国語で安心して受講いただけます。',
    },
    {
      icon: Shield,
      title: t('about.features.practical'),
      description: t('about.features.practicalDesc'),
      detail: '座学だけでなく、実際の建設機械やフォークリフトを使った実技訓練を重視。現場で即戦力となるスキルを身につけることができます。',
    },
    {
      icon: Headphones,
      title: t('about.features.support'),
      description: t('about.features.supportDesc'),
      detail: 'お申込みから受講、資格取得まで専任スタッフが丁寧にサポート。スケジュール調整や受講に関するご質問もお気軽にお問い合わせください。',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gray-900 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-504307651254-35680f356dfd?w=1920&auto=format&fit=crop"
            alt="Construction workers"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 lg:py-6">
          <div className="max-w-4xl">
            <h1 
              className="font-bold text-white mb-2 tracking-tight leading-tight whitespace-nowrap"
              style={{ fontSize: 'clamp(0.9rem, 4.5vw, 2.25rem)' }}
            >
              {t('hero.subtitle')}
            </h1>
            <p 
              className="text-[#00A651] font-semibold mb-3 whitespace-nowrap"
              style={{ fontSize: 'clamp(0.8rem, 3.5vw, 1.25rem)' }}
            >
              {t('hero.title')}
            </p>
            <p 
              className="text-gray-300 mb-4 leading-relaxed"
              style={{ fontSize: 'clamp(0.8rem, 3.2vw, 1.125rem)' }}
            >
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/courses">
                <Button 
                  className="w-full sm:w-auto bg-[#00A651] hover:bg-[#008a43] text-white font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-md"
                  data-testid="hero-cta"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-md"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="relative bg-[#00A651] py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white text-center">
              <div>
                <p className="text-2xl lg:text-3xl font-bold font-[Barlow_Condensed]">20+</p>
                <p className="text-xs text-white/80">{t('home.statsCourses')}</p>
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-bold font-[Barlow_Condensed]">8</p>
                <p className="text-xs text-white/80">{t('home.statsLanguages')}</p>
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-bold">{t('home.graduatesMany')}</p>
                <p className="text-xs text-white/80">{t('home.statsGraduates')}</p>
              </div>
              <div>
                <p className="text-2xl lg:text-3xl font-bold font-[Barlow_Condensed]">98%</p>
                <p className="text-xs text-white/80">{t('home.statsPassRate')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Calendar Section */}
      <section className="pt-4 bg-gray-50" style={{ paddingBottom: '20px' }} data-testid="schedule-calendar-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-3">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
              {t('home.scheduleTitle')}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t('home.scheduleDesc')}
            </p>
          </div>

          {schedulesLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">
                {t('home.noSchedules')}
              </p>
            </div>
          ) : (
            <ScheduleCalendar schedules={schedules} language={language} courses={courses} />
          )}

          <div className="text-center mt-3">
            <Link to="/apply">
              <Button 
                variant="outline"
                className="border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-white font-bold px-8"
                data-testid="view-all-schedules"
              >
                {t('home.viewAllCourses')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="pt-4 pb-3 lg:pt-6 lg:pb-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('courses.title')}
            </h2>
            <Link to="/courses" className="hidden md:inline-flex items-center gap-1 text-[#00A651] font-semibold hover:underline absolute right-0 top-2">
              {t('home.viewAll')}
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categories.slice(0, 5).map((category) => (
              <Link 
                key={category.id}
                to={`/courses?category=${category.id}`}
                className="px-4 py-2 bg-gray-100 hover:bg-[#e6f6ec] hover:text-[#00A651] rounded-full text-sm font-medium transition-colors"
                data-testid={`category-pill-${category.id}`}
              >
                {category.name[language] || category.name.ja}
              </Link>
            ))}
          </div>

          {/* Featured Courses Grid */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-course-grid">
              {displayCourses.map((course, idx) => (
                <div
                  key={course.id}
                  className={idx >= pinnedCourses.length ? 'animate-in fade-in duration-700' : ''}
                  data-testid={idx >= pinnedCourses.length ? `rotating-course-${idx}` : `featured-course-${idx}`}
                >
                  <CourseCard course={course} />
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-10 md:hidden">
            <Link to="/courses">
              <Button 
                className="bg-[#00A651] hover:bg-[#008a43] text-white font-bold px-8"
                data-testid="view-all-courses"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-2 pb-10 lg:pt-4 lg:pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.services')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-lg border-l-4 border-[#00A651] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`feature-${index}`}
                onClick={() => setSelectedFeature(feature)}
              >
                <feature.icon className="h-10 w-10 text-[#00A651] mb-4" />
                <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-8 pb-8 lg:pt-12 lg:pb-12 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('home.ctaDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply">
              <Button 
                className="bg-[#00A651] hover:bg-[#008a43] text-white font-bold px-8 py-6 text-lg"
                data-testid="cta-apply"
              >
                {t('nav.apply')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-6 text-lg"
                data-testid="cta-contact"
              >
                {t('nav.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Detail Dialog */}
      <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedFeature && <selectedFeature.icon className="h-6 w-6 text-[#00A651]" />}
              {selectedFeature?.title}
            </DialogTitle>
            <DialogDescription>{selectedFeature?.description}</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-gray-700 leading-relaxed">{selectedFeature?.detail}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
